import { handleRequest, routeNames } from "utils"
import { IChangePasswordRequest, ILoginRequest, ILoginResponse, IResetPasswordRequest, ISendOTPRequest, ISendOTPResponse, ISignUpRequest, ISignUpResponse, IVerifyOTPRequest } from "./types"
import { useAuthService } from "./service"
import { ApiResponse } from "utils/types";
import { useNotification } from "providers";
import { useProfileStore } from "store";
import { useApolloAuth } from "providers/apollo_auth";
import { useNavigate } from "react-router-dom";

export const useAuth = () => {
    const service = useAuthService();
    const notification = useNotification()
    const { logout } = useApolloAuth();
    const { reset } = useProfileStore();
    const navigate = useNavigate();
    const request:typeof handleRequest = (requestFn, input) => {
        return handleRequest(requestFn, input, notification)
    };
    return {
        logout(reroutePath: string = routeNames.login){
            reset();
            logout();
            if(reroutePath){
                navigate(reroutePath, { replace: true })
            }
        },
        changePassword(input: IChangePasswordRequest){
            return request(
                async (input: IChangePasswordRequest): Promise<ApiResponse<boolean>> => {
                try {
                    const { data, errors } = await service.changePassword({
                        variables: input
                    });
                    if(errors){
                        throw new Error(errors[0].message)
                    }
                    return {
                        success: true,
                        data: data?.changePassword.success,
                    }
                } catch (e) {
                    return {
                        err: (e as Error)
                    }
                }
            }, input)
        },
        resetPassword(input: Partial<IResetPasswordRequest>, token?: string){
            return request(
                async (input: Partial<IResetPasswordRequest>): Promise<ApiResponse<boolean>> => {
                try {
                    const { data, errors } = await service.resetPassword({
                        variables: input,
                        // set header with token
                        context: {
                            headers: {
                                "X-Otp-Verified-Access-Token": token || ""
                            }
                        }
                    });
                    if(errors){
                        throw new Error(errors[0].message)
                    }
                    return {
                        success: true,
                        data: data?.resetPassword.success,
                    }
                } catch (e) {
                    return {
                        err: (e as Error)
                    }
                }
            }, input)
        },
        verifyToken(input: Partial<IVerifyOTPRequest>){
            return request(
                async (input: Partial<IVerifyOTPRequest>): Promise<ApiResponse<string>> => {
                try {
                    const { data, errors } = await service.verifyToken({
                        variables: input
                    });
                    if(errors){
                        throw new Error(errors[0].message)
                    }
                    return {
                        success: true,
                        data: data?.verifyToken.otpVerifiedToken,
                    }
                } catch (e) {
                    return {
                        err: (e as Error)
                    }
                }
            }, input)
        },
        sendOTP(input: Partial<ISendOTPRequest>){
            return request(
                async (input: Partial<ISendOTPRequest>):Promise<ApiResponse<ISendOTPResponse>> => {
                    try {
                        const { data, errors } = await service.sendOTP({
                            variables: input
                        });
                        if(errors){
                            throw new Error(errors[0].message)
                        }
                        return {
                            success: true,
                            data: data?.sendOTP,
                        }
                    } catch (e) {
                        return {
                            err: (e as Error)
                        }
                    }
                }, 
                input
            )
        },
        singUp(input: ISignUpRequest){
            const { accessToken, ...otherInput } = input
            if(!accessToken){
                notification.error({message: "Access token is required for sign up"})
                return
            }
            return request(
                async (input: ISignUpRequest):Promise<ApiResponse<ISignUpResponse>> => {
                    try {
                        const { data, errors } = await service.signUp({
                            variables: otherInput,
                            // set header with token
                            context: {
                                headers: {
                                    "X-Otp-Verified-Access-Token": input.accessToken || ""
                                }
                            }
                        });
                        if(errors){
                            throw new Error(errors[0].message)
                        }
                        return {
                            success: true,
                            data: data?.signUp,
                        }
                    } catch (e) {
                        return {
                            err: (e as Error)
                        }
                    }
                }, 
                input
            )
        },
        login(input: ILoginRequest){
            return request(
                async (input: ILoginRequest):Promise<ApiResponse<ILoginResponse>> => {
                    try {
                        const { data, errors } = await service.login({
                            variables: input
                        });
                        if(errors){
                            throw new Error(errors[0].message)
                        }
                        return {
                            success: true,
                            data: data?.login,
                        }
                    } catch (e) {
                        return {
                            err: (e as Error)
                        }
                    }
                },
                input
            )
        }
    }
}